import React, { useState, useEffect, useRef} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronRight, ChevronLeft, Phone, PhoneCall, Printer, Mail, Play, Pause, CheckCircle, XCircle } from 'lucide-react';
import * as Icons from 'lucide-react';
import { Captcha, CaptchaHandle } from './components/Captcha';
import { useLanguage } from './contexts/LanguageContext';
import { getTranslation } from './lib/i18n';
import type { BlogPost } from './lib/blog';
import { ServiceCard } from './components/ServiceCard';
import type { Service, TargetGroup } from './lib/services';
import { TeamCarousel } from './components/TeamCarousel';
import { BlogCarousel } from './components/BlogCarousel';
import { TeamMember, getAboutContent, getAllTeamMembers } from './lib/about';

interface HomeProps {
  slides: Array<{
    title: string;
    description: string;
    image: string;
    icon: string;
    label: string;
  }>;
  services: Array<Service>;
  targetGroups: Array<TargetGroup>;
  servicesTitle: string;
  recentPosts: BlogPost[];
  contactTitle: string;
  contactDescription: string;
  buttonLabels: {
    details: string;
    readMore: string;
    submit: string;
  };
  currentSlide: number;
  goToSlide: (index: number) => void;
  goToPrevious: () => void;
  goToNext: () => void;
  formatDate: (date: string) => string;
  actualTickerHeight: number;
  isSliding: boolean;
  onTransitionEnd: () => void;
  autoSlideEnabled: boolean;
  toggleAutoSlide: () => void;
  language: string;
  onReady: () => void;
}

function Home({
  slides = [],
  services = [],
  targetGroups = [],
  servicesTitle,
  recentPosts = [],
  contactTitle,
  contactDescription,
  currentSlide,
  goToSlide,
  goToPrevious,
  goToNext,
  formatDate,
  actualTickerHeight,
  isSliding,
  onTransitionEnd,
  autoSlideEnabled,
  toggleAutoSlide,
  language,
  onReady
}: HomeProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { language: currentLanguage } = useLanguage();
  const [translations, setTranslations] = useState<any>({});
  const [heroHeight, setHeroHeight] = useState(window.innerHeight - actualTickerHeight);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [containerWidth, setContainerWidth] = useState(window.innerWidth);
  const [isCaptchaValid, setIsCaptchaValid] = useState(false);
  const captchaRef = useRef<CaptchaHandle>(null);
  const [activeTargetGroup, setActiveTargetGroup] = useState<string>('');
  const [cardRotations, setCardRotations] = useState<Record<string, number>>({});
  const [visibleCards, setVisibleCards] = useState<Set<string>>(new Set());
  const [isChangingGroup, setIsChangingGroup] = useState(false);
  const [displayedServices, setDisplayedServices] = useState<Service[]>([]);
  const [aboutContent, setAboutContent] = useState('');
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [formErrors, setFormErrors] = useState({
    name: false,
    email: false,
    message: false
  });

  const [contactInfo, setContactInfo] = useState<{
    tel: string;
    mobile: string;
    fax: string;
    email: string;
  }>({
    tel: '',
    mobile: '',
    fax: '',
    email: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);

  useEffect(() => {
    onReady();
  }, [onReady]);

  useEffect(() => {
    if (targetGroups.length > 0) {
      setActiveTargetGroup(targetGroups[0].key);
    }
  }, [targetGroups]);

  useEffect(() => {
    const loadContactInfo = async () => {
      const contact = await getTranslation(language, 'contact.office');
      setContactInfo(contact);
    };
    loadContactInfo();
  }, [language]);

  useEffect(() => {
    const loadTranslations = async () => {
      const [homeTranslations, formTranslations, navTranslations, contactFormTranslations] = await Promise.all([
        getTranslation(currentLanguage, 'home'),
        getTranslation(currentLanguage, 'form'),
        getTranslation(currentLanguage, 'nav'),
        getTranslation(currentLanguage, 'contactForm')
      ]);
      setTranslations({ home: homeTranslations, form: formTranslations, nav: navTranslations, contactForm: contactFormTranslations });
    };
    loadTranslations();
  }, [currentLanguage]);

  useEffect(() => {
    const loadAboutContent = async () => {
      const [content, members] = await Promise.all([
        getAboutContent(language),
        getAllTeamMembers()
      ]);
      setAboutContent(content);
      setTeamMembers(members);
    };
    loadAboutContent();
  }, [language]);

  useEffect(() => {
    const filteredServices = services.filter(service => 
      service.targetgroup === activeTargetGroup
    );

    if (isChangingGroup) {
      setVisibleCards(new Set());
      
      const timer1 = setTimeout(() => {
        setDisplayedServices(filteredServices);
        
        const timer2 = setTimeout(() => {
          setVisibleCards(new Set(filteredServices.map(s => s.id)));
          setIsChangingGroup(false);
        }, 50);
        
        return () => clearTimeout(timer2);
      }, 300);
      
      return () => clearTimeout(timer1);
    } else {
      setDisplayedServices(filteredServices);
      setVisibleCards(new Set(filteredServices.map(s => s.id)));
    }
  }, [activeTargetGroup, services, isChangingGroup]);

  const handleCardClick = (cardId: string) => {
    setCardRotations(prev => {
      const newRotations = { ...prev };
      
      Object.entries(newRotations).forEach(([id, rotation]) => {
        if (id !== cardId && rotation % 360 > 0) {
          newRotations[id] = rotation + 180;
        }
      });
      
      newRotations[cardId] = (newRotations[cardId] || 0) + 180;
      
      return newRotations;
    });
  };

  const handleTargetGroupChange = (groupKey: string) => {
    if (groupKey !== activeTargetGroup) {
      setIsChangingGroup(true);
      setActiveTargetGroup(groupKey);
      setTimeout(function () {
        setCardRotations({});
      }, 500);
    }
  };

  const allSlides = slides?.length > 0 ? [
    slides[slides.length - 1],
    ...slides,
    slides[0]
  ] : [];

  useEffect(() => {
    const handleResize = () => {
      setHeroHeight(window.innerHeight - actualTickerHeight);
      setIsMobile(window.innerWidth < 768);
      setContainerWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [actualTickerHeight]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setFormErrors(prev => ({
      ...prev,
      [name]: false
    }));
  };

  const validateForm = () => {
    const errors = {
      name: !formData.name,
      email: !formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email),
      message: !formData.message
    };
    setFormErrors(errors);
    return !Object.values(errors).some(error => error);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm() || !isCaptchaValid) {
      return;
    }
  
    setIsSubmitting(true);
    setSubmitStatus(null);
  
    try {
      const response = await fetch('https://rakudoitsu-contactform.azurewebsites.net/api/sendmail', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          message: formData.message
        }),
      });
  
      if (!response.ok) {
        throw new Error('Failed to send message');
      }
  
      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        message: ''
      });
      setIsCaptchaValid(false);
      captchaRef.current?.reset();
    } catch (error) {
      console.error('Error sending message:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getIconComponent = (iconName: string) => {
    const Icon = Icons[iconName as keyof typeof Icons];
    return Icon ? <Icon className="w-6 h-6" /> : null;
  };

  const getActualSlideIndex = (index: number) => {
    if (!allSlides.length) return 0;
    if (index === 0) return slides.length - 1;
    if (index === allSlides.length - 1) return 0;
    return index - 1;
  };

  const handleTransitionEnd = () => {
    setIsTransitioning(false);
    onTransitionEnd();
  };

  const scrollToServices = (slideIndex: number) => {
    const servicesSection = document.querySelector('#services-section');
    if (servicesSection) {
      servicesSection.scrollIntoView({ behavior: 'smooth' });
      
      if (language === 'ja') {
        const targetGroupMap: { [key: number]: string } = {
          0: 'hr',
          1: 'private',
          2: 'founder'
        };

        const targetGroup = targetGroupMap[slideIndex];
        if (targetGroup) {
          setIsChangingGroup(true);
          setActiveTargetGroup(targetGroup);
          setCardRotations({});
        }
      }
    }
  };

  if (!translations.home?.buttons) {
    return null;
  }

  if (!allSlides.length) {
    return null;
  }

  return (
    <>
      <div 
        className="relative"
        style={{ 
          marginTop: `${actualTickerHeight}px`,
          height: `${heroHeight}px`
        }}
      >
        <header className="relative h-full overflow-hidden">
          <div className="absolute inset-0">
            <div 
              className={`relative h-full flex ${(isSliding || isTransitioning) ? 'transition-transform duration-500 ease-in-out' : ''}`}
              style={{ 
                transform: `translateX(-${currentSlide * containerWidth}px)`,
                width: `${containerWidth * allSlides.length}px`
              }}
              onTransitionEnd={handleTransitionEnd}
            >
              {allSlides.map((slide, index) => (
                <div
                  key={index}
                  style={{ width: `${containerWidth}px` }}
                  className="h-full flex-shrink-0"
                >
                  <div className="relative h-full">
                    {slide?.image && (
                      <>
                        <img 
                          src={slide.image}
                          alt={slide.title || ''}
                          className="w-full h-full object-cover object-[50%_35%]"
                        />
                        <div className="absolute bottom-1 right-5 text-white/80 text-[8px] md:text-[10px] pointer-events-none z-10 drop-shadow-md">
                          AI generated image
                        </div>
                      </>
                    )}
                    <div className="absolute inset-0 bg-black/25">
                      <div className="container mx-auto px-4 h-full">
                        <div className="h-full flex flex-col justify-end pb-24 md:pb-48" style={{ paddingLeft: '4%' }}>
                          <div className="max-w-2xl">
                            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 md:mb-6 text-white leading-tight">
                              {slide?.title || ''}
                            </h2>
                            <p className="text-lg md:text-xl text-white mb-6 md:mb-8">
                              {slide?.description || ''}
                            </p>
                            <button 
                              onClick={() => scrollToServices(getActualSlideIndex(index))}
                              className="bg-white text-bamboo-800 px-6 md:px-8 py-2.5 md:py-3 rounded-lg flex items-center gap-2 hover:bg-bamboo-50 transition-all text-sm md:text-base"
                            >
                              {translations.home.buttons.details} <ChevronRight size={20} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="absolute inset-x-0 bottom-4 flex justify-between items-center px-4">
              <button
                onClick={goToPrevious}
                className="p-2 rounded-full bg-black/20 hover:bg-black/40 transition-colors backdrop-blur-sm"
                disabled={isTransitioning}
              >
                <ChevronLeft className="w-8 h-8 text-white" />
              </button>

              <div className="flex justify-center space-x-4 md:space-x-8">
                {slides.map((slide, index) => (
                  <button
                    key={index}
                    onClick={() => goToSlide(index)}
                    className="group relative"
                    disabled={isTransitioning}
                  >
                    <div className="relative">
                      <div className={`bg-black/20 backdrop-blur-sm rounded-xl p-3 md:p-4 transition-all duration-300 ${
                        getActualSlideIndex(currentSlide) === index ? 'bg-black/30' : ''
                      }`}>
                        <div className="text-white">
                          {slide.icon && getIconComponent(slide.icon)}
                        </div>
                      </div>
                      
                      <div className={`absolute inset-0 border-2 rounded-xl transition-all duration-300 ${
                        getActualSlideIndex(currentSlide) === index ? 'border-white' : 'border-transparent group-hover:border-white/50'
                      }`}></div>
                    </div>
                    
                    <div className="hidden md:block absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-3 py-1.5 bg-white/90 backdrop-blur-sm rounded-lg text-bamboo-800 text-sm whitespace-nowrap opacity-0 transform -translate-y-2 pointer-events-none transition-all duration-300 group-hover:opacity-100 group-hover:translate-y-0">
                      {slide.label}
                      <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px] border-t-white/90"></div>
                    </div>
                  </button>
                ))}
              </div>

              <button
                onClick={goToNext}
                className="p-2 rounded-full bg-black/20 hover:bg-black/40 transition-colors backdrop-blur-sm"
                disabled={isTransitioning}
              >
                <ChevronRight className="w-8 h-8 text-white" />
              </button>
            </div>

            <button
              onClick={toggleAutoSlide}
              className="absolute top-24 right-4 w-10 h-10 rounded-full bg-black/20 hover:bg-black/40 transition-colors backdrop-blur-sm flex items-center justify-center"
            >
              {autoSlideEnabled ? (
                <Pause className="w-5 h-5 text-white" />
              ) : (
                <Play className="w-5 h-5 text-white translate-x-[1px]" />
              )}
            </button>
          </div>
        </header>
      </div>

      <section className="py-20 bg-bamboo-50" id="services-section">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-12 md:mb-16">
            {servicesTitle}
          </h2>
          
          {targetGroups.length > 1 && (
            <div className="flex justify-center mb-12">
              <div className="flex flex-col md:inline-flex md:flex-row rounded-lg bg-bamboo-100/50 p-1 w-full md:w-auto">
                {targetGroups.map((group) => (
                  <button
                    key={group.key}
                    onClick={() => handleTargetGroupChange(group.key)}
                    className={`px-8 py-3 rounded-lg text-lg font-bold transition-colors mb-1 md:mb-0 ${
                      activeTargetGroup === group.key
                        ? 'bg-white text-bamboo-800 shadow-sm'
                        : 'text-bamboo-600 hover:text-bamboo-700'
                    }`}
                  >
                    {group.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8 md:gap-12 xl:gap-16 min-h-[800px]">
            {displayedServices.map((service) => (
              <ServiceCard
                key={service.id}
                id={service.id}
                title={service.title}
                subheadline={service.subheadline}
                teaser={service.teaser}
                description={service.description}
                icon={service.icon}
                rotationDegrees={cardRotations[service.id] || 0}
                onCardClick={handleCardClick}
                isVisible={visibleCards.has(service.id)}
              />
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white" id="about-section">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-12 md:mb-16">
            {translations.nav?.about_us || '会社概要'}
          </h2>
          <div className="max-w-3xl mx-auto mb-16">
            <div 
              className="prose prose-lg max-w-none"
              dangerouslySetInnerHTML={{ __html: aboutContent }}
            />
          </div>

          {teamMembers.length > 0 && (
            <div className="mt-16">
              <h3 className="text-xl md:text-2xl font-bold text-center mb-12 text-gray-600">
                {translations.home?.sections?.team}
              </h3>
              <TeamCarousel members={teamMembers} />
            </div>
          )}
        </div>
      </section>

      {recentPosts.length > 0 && (
        <section className="py-20" id="blog-section">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-12 md:mb-16">{translations.home.sections.blog}</h2>
            <BlogCarousel 
              posts={recentPosts}
              formatDate={formatDate}
              translations={translations.home}
            />
          </div>
        </section>
      )}

      <section className="py-20" id="contact-section">
        <div className="container mx-auto px-4">
          <div className="bg-bamboo-700 rounded-2xl p-8 md:p-12 text-white">
            <div className="grid md:grid-cols-2 gap-12">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold mb-6">{contactTitle}</h2>
                <p className="mb-8">{contactDescription}</p>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5" />
                    <span>Tel (Office) {contactInfo.tel}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <PhoneCall className="w-5 h-5" />
                    <span>Mobile {contactInfo.mobile}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Printer className="w-5 h-5" />
                    <span>Fax {contactInfo.fax}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5" />
                    <span>{contactInfo.email}</span>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg p-6">
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder={translations.form?.name?.placeholder}
                      className={`w-full px-4 py-2 rounded border text-gray-900 ${
                        formErrors.name ? 'border-red-500' : 'border-gray-300'
                      } focus:border-bamboo-500 focus:ring-2 focus:ring-bamboo-200`}
                      disabled={isSubmitting}
                    />
                    {formErrors.name && (
                      <p className="text-red-500 text-sm mt-1">
                        {translations.form?.name?.error}
                      </p>
                    )}
                  </div>
                  <div>
                    <input
                      type="text"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder={translations.form?.email?.placeholder}
                      className={`w-full px-4 py-2 rounded border text-gray-900 ${
                        formErrors.email ? 'border-red-500' : 'border-gray-300'
                      } focus:border-bamboo-500 focus:ring-2 focus:ring-bamboo-200`}
                      disabled={isSubmitting}
                    />
                    {formErrors.email && (
                      <p className="text-red-500 text-sm mt-1">
                        {translations.form?.email?.error}
                      </p>
                    )}
                  </div>
                  <div>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder={translations.form?.message?.placeholder}
                      rows={4}
                      className={`w-full px-4 py-2 rounded border text-gray-900 ${
                        formErrors.message ? 'border-red-500' : 'border-gray-300'
                      } focus:border-bamboo-500 focus:ring-2 focus:ring-bamboo-200`}
                      disabled={isSubmitting}
                    />
                    {formErrors.message && (
                      <p className="text-red-500 text-sm mt-1">
                        {translations.form?.message?.error}
                      </p>
                    )}
                  </div>
                  <div>
                    <Captcha ref={captchaRef} onValidate={setIsCaptchaValid} />
                  </div>
                  <button 
                    type="submit"
                    className={`w-full py-2 rounded transition-colors flex items-center justify-center gap-2 ${
                      isCaptchaValid && !isSubmitting
                        ? 'bg-bamboo-600 hover:bg-bamboo-700 text-white'
                        : 'bg-gray-300 text-gray-500'
                    }`}
                    disabled={!isCaptchaValid || isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>{translations.contactForm?.sending}</span>
                      </>
                    ) : (
                      translations.home.buttons.submit
                    )}
                  </button>
                  {submitStatus === 'success' && (
                    <div className="flex items-center gap-2 text-bamboo-600">
                      <CheckCircle className="w-5 h-5" />
                      <span>{translations.contactForm?.success}</span>
                    </div>
                  )}
                  {submitStatus === 'error' && (
                    <div className="flex items-center gap-2 text-red-500">
                      <XCircle className="w-5 h-5" />
                      <span>{translations.contactForm?.error}</span>
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default Home;